import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./SettingsContent.css";
import { LightModeContext } from "../../../hooks/mode/LightModeContextProvider";
import { Collapse } from "react-collapse";
import { LANGUAGES } from "../../../constants/defaults";

const ToggleSwitch = ({ checked, onChange, name, color = "#b10511" }) => (
  <label
    className="contour-toggle-label"
    data-testid={`settings-toggle-${name}`}
  >
    <input
      type="checkbox"
      className="contour-toggle-input"
      checked={checked}
      onChange={(e) =>
        onChange({ target: { checked: e.target.checked, name } })
      }
      name={name}
      data-testid={`settings-toggle-input-${name}`}
    />
    <span className="contour-toggle-track" style={{ "--toggle-color": color }}>
      <span className="contour-toggle-thumb" />
    </span>
  </label>
);

const LanguageDropdown = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang =
    LANGUAGES.find(
      (l) =>
        l.code === (i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2),
    ) || LANGUAGES[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="settings-lang"
      ref={ref}
      data-testid="settings-language-dropdown"
    >
      <button
        type="button"
        className={`settings-lang-toggle ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="settings-language-toggle"
      >
        <img
          src={currentLang.flag}
          alt={currentLang.label}
          className="settings-lang-flag"
        />
        <span className="settings-lang-label">{currentLang.label}</span>
        <svg
          className="settings-lang-chevron"
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <ul className="settings-lang-menu" role="listbox">
          {LANGUAGES.map((lang) => (
            <li
              key={lang.code}
              role="option"
              aria-selected={lang.code === currentLang.code}
              className={`settings-lang-item ${
                lang.code === currentLang.code ? "selected" : ""
              }`}
              onClick={() => changeLanguage(lang.code)}
              data-testid={`settings-language-option-${lang.code}`}
            >
              <img
                src={lang.flag}
                alt={lang.label}
                className="settings-lang-flag"
              />
              <span>{lang.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SettingsContent = ({
  scrollZoom,
  setScrollZoom,
  dragRotate,
  setDragRotate,
  keyboard,
  setKeyboard,
  showClock,
  setShowClock,
  showCursorCoords,
  setShowCursorCoords,
  isSmallScreen,
  openSection,
}) => {
  const { t } = useTranslation();
  const { toggle, isLightMode } = useContext(LightModeContext);

  const handleToggle = (event, setter) => {
    setter(event.target.checked);
  };

  const switches = (
    <>
      <div className="settings-leftside">
        <div className="switch-item">
          <p>{t("settings.theme")}</p>
          <ToggleSwitch
            checked={!isLightMode}
            onChange={(e) => handleToggle(e, toggle)}
            name="themeSwitch"
          />
        </div>
        <div className="switch-item">
          <p>{t("settings.showClock")}</p>
          <ToggleSwitch
            checked={showClock}
            onChange={(e) => handleToggle(e, setShowClock)}
            name="showClock"
          />
        </div>
        <div className="switch-item">
          <p>{t("settings.showCursorCoords")}</p>
          <ToggleSwitch
            checked={showCursorCoords}
            onChange={(e) => handleToggle(e, setShowCursorCoords)}
            name="showCursorCoords"
          />
        </div>
        <div className="switch-item">
          <p>{t("settings.language")}</p>
          <LanguageDropdown />
        </div>
      </div>
      <div className="settings-rightside">
        <div className="switch-item">
          <p>{t("settings.scrollZoom")}</p>
          <ToggleSwitch
            checked={scrollZoom}
            onChange={(e) => handleToggle(e, setScrollZoom)}
            name="scrollZoom"
          />
        </div>
        <div className="switch-item">
          <p>{t("settings.dragRotate")}</p>
          <ToggleSwitch
            checked={dragRotate}
            onChange={(e) => handleToggle(e, setDragRotate)}
            name="dragRotate"
          />
        </div>
        <div className="switch-item">
          <p>{t("settings.keyboard")}</p>
          <ToggleSwitch
            checked={keyboard}
            onChange={(e) => handleToggle(e, setKeyboard)}
            name="keyboard"
          />
        </div>
      </div>
    </>
  );

  return (
    <>
      {isSmallScreen ? (
        <Collapse isOpened={openSection === "settings"}>
          <div className="settings-container">{switches}</div>
        </Collapse>
      ) : (
        <div className="settings-container">{switches}</div>
      )}
    </>
  );
};

export default SettingsContent;